// src/components/Popups/AppearanceSettingsPopup.jsx
//
// Fereastra „Setări” a elevului (butonul cu rotiță din josul meniului):
// tema, culoarea de bază, mărimea textului și animațiile. Totul se aplică
// pe loc și rămâne salvat pe dispozitiv (utils/appearanceSettings).

import React, { useEffect, useState } from "react";

import SegmentedToggle from "../Common/SegmentedToggle";
import UIToggle from "../Common/UIToggle";

import {
   ACCENTS,
   DEFAULTS,
   TEXT_SIZES,
   getAppearance,
   setAppearance,
   subscribeAppearance,
} from "../../utils/appearanceSettings";

const THEMES = [
   { value: "dark", label: "Întunecată" },
   { value: "light", label: "Luminoasă" },
];

// rândurile și comutatoarele sunt ca în restul platformei: lista din profil
// (studentSelfProfilePopup), comutatoarele din Common (SegmentedToggle,
// UIToggle) și, pentru cele trei mărimi, grupul de butoane de la Testare
function Row({ title, hint, stack = false, children }) {
   return (
      <div className={"appearancePopup__row" + (stack ? " appearancePopup__row--stack" : "")}>
         <div className="appearancePopup__label">
            <span>{title}</span>
            {hint && <small>{hint}</small>}
         </div>
         {children}
      </div>
   );
}

function Choice({ options, value, onChange, label, renderOption }) {
   return (
      <div className="practice-toolbar__group appearancePopup__choice" role="radiogroup" aria-label={label}>
         {options.map((option) => (
            <button
               key={option.key}
               type="button"
               role="radio"
               aria-checked={value === option.key}
               className={
                  "practice-toolbar__btn" +
                  ` appearancePopup__option--${option.key}` +
                  (value === option.key ? " is-active" : "")
               }
               onClick={() => onChange(option.key)}
            >
               {renderOption ? renderOption(option) : option.label}
            </button>
         ))}
      </div>
   );
}

function CheckIcon() {
   return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
         <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
      </svg>
   );
}

export default function AppearanceSettingsPopup() {
   const [settings, setSettings] = useState(getAppearance);
   useEffect(() => subscribeAppearance(setSettings), []);

   const isDefault =
      settings.accent === DEFAULTS.accent &&
      settings.textSize === DEFAULTS.textSize &&
      settings.motion === DEFAULTS.motion;

   return (
      <div className="appearancePopup">
         <div className="appearancePopup__header">
            <h3 className="appearancePopup__title">Setări</h3>
            <p className="appearancePopup__lead">
               Cum arată platforma pentru tine. Se salvează pe acest dispozitiv.
            </p>
         </div>

         <div className="appearancePopup__content">
            <div className="appearancePopup__list">
               <Row title="Tema" stack>
                  <SegmentedToggle
                     name="appearance-theme"
                     aria-label="Tema"
                     options={THEMES}
                     value={settings.theme}
                     onChange={(theme) => setAppearance({ theme })}
                  />
               </Row>

               <Row
                  title="Culoarea de bază"
                  hint="Butoanele, evidențierile și lecția următoare."
                  stack
               >
                  <div className="appearancePopup__accents" role="radiogroup" aria-label="Culoarea de bază">
                     {ACCENTS.map((accent) => {
                        const active = settings.accent === accent.key;
                        return (
                           <button
                              key={accent.key}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              title={accent.label}
                              className={"appearancePopup__accent" + (active ? " is-active" : "")}
                              style={{
                                 "--swatch":
                                    settings.theme === "light" ? accent.light : accent.dark,
                              }}
                              onClick={() => setAppearance({ accent: accent.key })}
                           >
                              <span className="appearancePopup__swatch" aria-hidden="true">
                                 {active && <CheckIcon />}
                              </span>
                              <span className="appearancePopup__accentLabel">
                                 {accent.label}
                              </span>
                           </button>
                        );
                     })}
                  </div>
               </Row>

               <Row title="Mărimea textului" stack>
                  <Choice
                     label="Mărimea textului"
                     options={TEXT_SIZES}
                     value={settings.textSize}
                     onChange={(textSize) => setAppearance({ textSize })}
                     renderOption={(size) => (
                        <>
                           <span className="appearancePopup__sample" aria-hidden="true">
                              Aa
                           </span>
                           {size.label}
                        </>
                     )}
                  />
                  <p className="appearancePopup__preview">
                     Așa arată textul: următoarea lecție e mâine la 10:30.
                  </p>
               </Row>

               <Row
                  title="Animații"
                  hint="Oprește mișcările dacă te deranjează sau telefonul e lent."
               >
                  <UIToggle
                     aria-label="Animații în pagini"
                     checked={settings.motion === "full"}
                     onChange={(on) =>
                        setAppearance({ motion: on ? "full" : "reduced" })
                     }
                  />
               </Row>
            </div>

            <button
               type="button"
               className="appearancePopup__reset"
               disabled={isDefault}
               onClick={() =>
                  setAppearance({
                     accent: DEFAULTS.accent,
                     textSize: DEFAULTS.textSize,
                     motion: DEFAULTS.motion,
                  })
               }
            >
               Revino la setările inițiale
            </button>
         </div>
      </div>
   );
}
